import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, Loader2, Sparkles } from "lucide-react";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState("monthly");
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [payEnabled, setPayEnabled] = useState(true);
  const [busy, setBusy] = useState(null);
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/payments/config").then((r) => setPayEnabled(r.data.enabled)).catch(() => {});
  }, []);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon });
      setApplied(data);
      toast.success(`${data.discount_percent}% off applied`);
    } catch (e) {
      setApplied(null);
      toast.error(e.response?.data?.detail || "Invalid coupon");
    }
  };

  const priceOf = (p) => {
    const base = billing === "annual" ? p.price_annual : p.price_monthly;
    if (applied && base > 0) return Math.round(base * (1 - applied.discount_percent / 100));
    return base;
  };

  const subscribe = async (plan) => {
    if (!user) { navigate("/login"); return; }
    setBusy(plan.id);
    try {
      const { data } = await api.post("/payments/create-order", {
        plan_id: plan.id, billing, coupon: applied ? applied.code : null,
      });
      if (data.free) {
        await refreshUser();
        toast.success(`You're on ${plan.name}`);
        setBusy(null);
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway"); setBusy(null); return; }
      const rz = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "KalamPrime",
        description: `${data.plan_name} — ${billing}`,
        order_id: data.order_id,
        theme: { color: "#0066FF" },
        prefill: { name: user.name, email: user.email },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", { ...resp, plan_id: plan.id });
            await refreshUser();
            toast.success(`Subscribed to ${plan.name}!`);
          } catch {
            toast.error("Payment verification failed");
          }
        },
        modal: { ondismiss: () => setBusy(null) },
      });
      rz.open();
      setBusy(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  return (
    <Layout>
      <div className="pt-28 px-4 md:px-12 pb-16 max-w-6xl mx-auto">
        <div className="text-center fade-up">
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white">Choose your plan</h1>
          <p className="text-slate-400 mt-3">Only on KalamPrime. Cancel anytime.</p>
          {user && (
            <p className="text-sm mt-2 text-[var(--nik-gold)]">Current plan: {user.subscription_plan?.toUpperCase()}</p>
          )}
        </div>

        <div className="flex justify-center mt-6">
          <div className="inline-flex bg-white/5 rounded-full p-1">
            {["monthly", "annual"].map((b) => (
              <button key={b} data-testid={`billing-${b}`} onClick={() => setBilling(b)}
                className={`px-5 py-2 rounded-full text-sm font-semibold capitalize transition-colors ${billing === b ? "bg-[var(--nik-blue)] text-white" : "text-slate-300"}`}>
                {b} {b === "annual" && <span className="text-[var(--nik-gold)] text-xs ml-1">Save more</span>}
              </button>
            ))}
          </div>
        </div>

        {!payEnabled && (
          <div className="mt-6 max-w-lg mx-auto text-center text-sm text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-3">
            Paid checkout is not configured yet. Add Razorpay keys to enable subscriptions.
          </div>
        )}

        <div className="flex items-center justify-center gap-2 mt-6">
          <input
            data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value)}
            placeholder="Coupon code (try KALAM50)"
            className="bg-[#121824] border border-white/10 rounded-lg px-4 py-2 text-white text-sm outline-none focus:border-[var(--nik-blue)]"
          />
          <button data-testid="apply-coupon-btn" onClick={applyCoupon} className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors">
            Apply
          </button>
        </div>

        <div className="flex flex-wrap justify-center gap-5 mt-10">
          {plans.map((p) => {
            const price = priceOf(p);
            const isCurrent = user?.subscription_plan === p.id;
            return (
              <div key={p.id}
                className={`w-full sm:w-[340px] relative rounded-2xl p-6 border flex flex-col ${p.badge ? "border-[var(--nik-blue)] bg-gradient-to-b from-[#12204a] to-[#121824] nik-glow-btn" : "border-white/10 bg-[#121824]"}`}>
                {p.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--nik-gold)] text-black text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <Sparkles size={12} /> {p.badge}
                  </span>
                )}
                <h3 className="font-display text-lg font-bold text-white">{p.name}</h3>
                <div className="mt-3 flex items-end gap-1">
                  <span className="font-display text-3xl font-extrabold text-white">₹{price}</span>
                  <span className="text-slate-400 text-sm mb-1">/{billing === "annual" ? "yr" : "mo"}</span>
                </div>
                {applied && price > 0 && (
                  <span className="text-xs text-[var(--nik-gold)] mt-1">{applied.discount_percent}% off applied</span>
                )}
                <div className="text-xs text-slate-400 mt-2">{p.quality} • {p.screens} screen{p.screens > 1 ? "s" : ""}</div>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check size={16} className="text-[var(--nik-glow)] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid="subscription-plan-cta"
                  disabled={isCurrent || busy === p.id || (price > 0 && !payEnabled)}
                  onClick={() => subscribe(p)}
                  className={`mt-6 w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                    isCurrent ? "bg-white/10 text-slate-400 cursor-default"
                      : p.badge ? "bg-[var(--nik-blue)] hover:bg-[var(--nik-blue-hover)] text-white"
                      : "bg-white text-black hover:bg-slate-200"
                  } disabled:opacity-60`}
                >
                  {busy === p.id && <Loader2 size={16} className="animate-spin" />}
                  {isCurrent ? "Current Plan" : price === 0 ? "Get Started" : "Subscribe"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
